import React from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useRouteChangeHandler } from 'shared/hooks';
import { useInfoStore } from 'store/useInfoStore';

import { NavList } from '../constants';
import AppHeaderItem from './AppHeaderItem';
import AuthButtons from './AuthButtons';
import HamburgerMenu from './HamburgerMenu';
import LanguageSwitcher from './LanguageSwitcher';
import ServiceIcons from './ServiceIcons';

const AppHeader = () => {
  const { t } = useTranslation();
  const { handleRouteChange } = useRouteChangeHandler();
  const { accessToken, memberRole } = useInfoStore();

  const { pathname, search } = useLocation();

  const isLogin = !!accessToken;

  const translatedNavList = NavList.map((item) => ({
    ...item,
    title: t(item.titleKey),
    showNewBadge: item.showNewBadge,
    subMenu:
      item.subMenu === 'services'
        ? ('services' as const)
        : item.subMenu?.map((subItem) => ({
            ...subItem,
            title: t(subItem.titleKey),
          })),
  }));

  return (
    // 데스크톱 GNB(로고+메뉴 5개+우측 컨트롤)는 한 줄에 최소 ~1320px 가 필요해서,
    // 그 미만에서는 모바일 헤더(로고+아이콘+햄버거)를 유지한다. md(800px) 에서 전환하면
    // 800~1319px 구간에서 로고가 압착되어 사라지고 우측 컨트롤이 잘림.
    // left-0 + max-w-[100vw]: html min-width(1280px) 로 캔버스가 뷰포트보다 넓어져도
    // 헤더는 뷰포트 폭에 고정(sticky 는 left 오프셋으로 가로축에도 고정됨) — 햄버거가 항상 보이게.
    <header className="sticky left-0 top-0 z-30 flex h-[60px] w-full max-w-[100vw] items-center justify-center bg-white min-[1320px]:h-[99px]">
      <div
        data-nosnippet
        className="mx-auto flex w-full max-w-[1480px] items-center justify-between px-4 py-3 min-[1320px]:px-[60px] min-[1320px]:py-[30px]"
      >
        <a
          href={`${pathname}${search}#footer-content`}
          className="skip-link fixed left-0 right-0 top-0 z-[100] -translate-y-full bg-black p-2 text-center text-white outline-none transition-transform duration-150 focus:translate-y-0"
        >
          Skip to main footer
        </a>

        {/* Left side - Logo and Navigation */}
        <div className="flex flex-1 items-center gap-[60px]">
          <button
            onClick={() => {
              window.dataLayer?.push({
                event: 'click_menu',
                menu: 'logo',
                section: 'Header',
              });
              handleRouteChange('/');
            }}
          >
            <img
              src="/images/optimized/checkmate_logo.webp"
              alt="CheckmateKorea"
              className="h-6 w-auto cursor-pointer min-[1320px]:h-8"
            />
          </button>

          <nav className="hidden items-center gap-3 min-[1320px]:flex">
            {translatedNavList.map(({ title, to, target, subMenu, showNewBadge, gaLabel }, idx) => (
              <AppHeaderItem
                key={idx}
                title={title}
                to={to}
                target={target}
                subMenu={subMenu}
                showNewBadge={showNewBadge}
                gaLabel={gaLabel}
                memberRole={memberRole}
              />
            ))}
          </nav>
        </div>

        {/* Right side - Actions */}
        <div className="flex items-center gap-1">
          {!isLogin ? (
            <>
              <div className="hidden min-[1320px]:flex min-[1320px]:items-center min-[1320px]:gap-3">
                <AuthButtons />
              </div>
              <LanguageSwitcher />
              <HamburgerMenu isLogin={false} />
            </>
          ) : (
            <>
              <ServiceIcons />
              <LanguageSwitcher />
              <HamburgerMenu isLogin={true} />
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
